









import { Component, Vue } from 'vue-property-decorator'
import { ReferralCount } from '@/api/types'
import { DoughnutChart } from 'vue-chart-3'
import { Chart, ChartData, ChartOptions, DoughnutController, ArcElement, ChartEvent, LegendItem, LegendElement, ActiveElement } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { namespace } from 'vuex-class'
const referrals = namespace('referrals')
Chart.register(DoughnutController, ArcElement)

@Component({
    components: {
        DoughnutChart,
    },
})
export default class LegacyChartReferralTypes extends Vue {
    @referrals.State readonly referralCounts!: ReferralCount
    @referrals.State readonly typeFilter!: string[]
    @referrals.Action applyTypeFilter: ({ typeFilter: [] }) => Promise<void>

    onChartRender(chart: Chart) {
        // @ts-ignore
        const legendItems = chart.legend.legendItems
        legendItems.forEach((item, index) => {
            const key = Object.keys(this.chartValues)[index]
            item.hidden = this.typeFilter.length > 0 && !this.typeFilter.includes(key)
        })
    }

    get chartData(): ChartData {
        if (!this.chartValues) {
            return null
        }

        return {
            labels: this.chartLabels,
            datasets: [
                {
                    data: Object.values(this.chartValues),
                    backgroundColor: this.chartColors,
                    datalabels: {
                        color: 'rgba(255, 255, 255, .75)',
                    },
                },
            ],
        }
    }

    get chartOptions(): ChartOptions<'doughnut'> {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 4,
            animation: {
                easing: 'easeOutExpo',
            },
            plugins: {
                legend: {
                    position: 'right',
                    onClick: (e: ChartEvent, legendItem: LegendItem, legend: LegendElement) => {
                        // @ts-ignore type defines datasetIndex but it is index at runtime
                        this.onClick(legendItem.index)
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
                if (elements.length === 0) {
                    return
                }

                this.onClick(elements[0].index)
            },
        }
    }

    get chartPlugins(): object[] {
        return [
            ChartDataLabels,
        ]
    }

    get chartValues(): {[key: string]: number} {
        if (!this.referralCounts) {
            return {}
        }

        const chartValues = {
            'appraisal': this.referralCounts.typeAppraisal,
            'epa': this.referralCounts.typeEpa,
            'information': this.referralCounts.typeInformation,
            'mortgage': this.referralCounts.typeMortgage,
            'purchase': this.referralCounts.typePurchase,
            'photo': this.referralCounts.typePhoto,
            'sale': this.referralCounts.typeSale,
            'sign-placement': this.referralCounts.typeSignPlacement,
            'taxation': this.referralCounts.typeTaxation,
            'technical-appraisal': this.referralCounts.typeTechnicalAppraisal,
            'new-build': this.referralCounts.typeNewBuild,
        }

        Object.keys(chartValues).forEach(key => {
            if (chartValues[key] === 0 && !this.typeFilter.includes(key)) {
                delete chartValues[key]
            }
        })

        return chartValues
    }

    get chartLabels(): string[] {
        const chartLabels = []
        Object.keys(this.chartValues).forEach(key => {
            const label = this.$t(`referral.type.${key}`)
            chartLabels.push(`${label} (${this.chartValues[key]})`)
        })

        return chartLabels
    }

    get chartColors(): string[] {
        const colors = {
            'appraisal': 'rgba(19, 210, 197, 1)',
            'epa': 'rgba(235, 89, 140, 1)',
            'information': 'rgba(247, 131, 35, 1)',
            'mortgage': 'rgba(66, 153, 225, 1)',
            'purchase': 'rgba(145, 105, 226, 1)',
            'photo': 'rgba(231, 220, 30, 1)',
            'sale': 'rgba(0, 163, 137, 1)',
            'sign-placement': 'rgba(222, 64, 119, 1)',
            'taxation': 'rgb(125,222,64)',
            'technical-appraisal': 'rgb(198,222,64)',
            'new-build': 'rgb(64,67,222)',
        }

        const chartColors = []
        Object.keys(this.chartValues).forEach(key => {
            const color = colors[key]
            chartColors.push(color)
        })

        return chartColors
    }

    onClick(selectedIndex: number): void {
        const selectedKey = Object.keys(this.chartValues)[selectedIndex]

        let typeFilter = [].concat(this.typeFilter)
        if (!typeFilter.includes(selectedKey)) {
            typeFilter.push(selectedKey)
        } else {
            typeFilter = typeFilter.filter(key => key !== selectedKey)
        }

        this.applyTypeFilter({ typeFilter })
    }
}
